import type { RentDuration } from "@/features/devices/types";

/**
 * Verbatim `/` (landing) copy, lifted 1:1 from live xgodo.com and cross-checked
 * against the legacy source — `frontend/src/containers/home/Landing_page.tsx`
 * (the hero) and `frontend/src/app/(Devices)/devices/marketplace/device_list.tsx`
 * (the card + search, which the landing renders directly).
 *
 * Single content source for the page: copy and parity rules live here, layout
 * lives in `landing-view`. Changing a string here is a parity decision; changing
 * `landing-view` is a design decision.
 *
 * WHAT THE LIVE LANDING ACTUALLY IS. Worth stating plainly, because the v5 page
 * this replaces had drifted a long way from it: signed out, xgodo.com is one
 * headline followed immediately by the searchable device marketplace. There is
 * no feature grid, no "why us" section, no testimonial band and no closing CTA
 * — the devices ARE the pitch. The redesign keeps that shape. Re-adding a
 * feature grid, a "why us" band or a closing CTA would recreate exactly the
 * drift this page was rebuilt to undo.
 *
 * NOT on the live signed-out landing, and therefore not here:
 *  - a footer (v5 has none anywhere — b520fc3)
 *  - pagination: legacy renders the pager only when `user_id` is set, so a
 *    signed-out visitor sees the first 9 devices and nothing more
 *  - any price the backend didn't offer — durations with no price are omitted
 *    from the selector rather than shown as "—"
 */

/** The one and only headline. Live's title-case is deliberate — keep it. */
export const LANDING_TITLE = "Rent And Automate Real Remote Phones";

/** Search, verbatim from the legacy `SearchBar`. */
export const LANDING_SEARCH = {
  placeholder: "Search devices...",
  submit: "Search",
  /** Accessible name; live leans on the placeholder alone, which fails a11y. */
  label: "Search devices",
} as const;

/** Device card, verbatim from the legacy market card. */
export const LANDING_CARD = {
  /** Owner byline — rendered as `By {username}`, linking to their profile. */
  byPrefix: "By",
  rent: "Rent Now",
  /** Spec rows, in live's order. Label left, value right. */
  specs: {
    name: "Name",
    version: "Version",
    ram: "RAM",
    processor: "Processor",
  },
} as const;

/** Spec row order as it appears on the live card. */
export const LANDING_SPEC_ORDER = [
  "name",
  "version",
  "ram",
  "processor",
] as const;

/**
 * Duration suffixes in the price selector, matching live exactly — "$0.12/hr",
 * "$3/day", "$24/wk", "$80/mo". Legacy calls this `rentDurationAbbreviation`.
 */
export const DURATION_ABBREVIATION: Record<RentDuration, string> = {
  hourly: "hr",
  daily: "day",
  weekly: "wk",
  monthly: "mo",
};

/** Selector order; also the order the "cheapest offered" scan walks. */
export const DURATION_ORDER: RentDuration[] = [
  "hourly",
  "daily",
  "weekly",
  "monthly",
];

/** Devices per page — legacy `PAGE_SIZE`, shared by the landing and the real
 * marketplace so the first screen matches between them. */
export const LANDING_PAGE_SIZE = 9;

/**
 * How many devices the landing actually fetches.
 *
 * The stat strip states fleet-wide facts — "Online now", "Countries", "From
 * $x" — but derives them from the devices in hand, while "Devices available"
 * shows the server's total. Fetching only `LANDING_PAGE_SIZE` made three of the
 * four wrong whenever the answer lived past the first page: with 16 devices on
 * the marketplace the strip read "Online now 0 / Countries 1" when the true
 * figures were 1 and 2 (verified 2026-08-14). There is no fleet-aggregate
 * endpoint, so the strip needs the fleet; the grid still shows the first
 * `LANDING_PAGE_SIZE`.
 */
export const LANDING_STATS_LIMIT = 100;

/**
 * Where "Rent Now" goes for a signed-out visitor. Renting needs an account, and
 * v5 has no login modal (legacy pops one), so the button navigates to the
 * marketplace behind auth — `middleware.ts` bounces it to /login with a
 * callbackUrl, landing the visitor back on the device they picked. This is the
 * same destination legacy uses for its signed-out fallback.
 */
export const RENT_HREF = "/devices/marketplace";

/**
 * Scenario seam. Unlike the form pages (/contact, /forgot), the landing's only
 * stateful region is a real react-query list, so it needs no local scenario
 * prop: `?scenario=off|loading|error` is already applied app-wide by the global
 * MSW handler, and `empty` is served through the `emptyable` list wrapper — see
 * `lib/demo-config.ts` and the demo-scenario seam notes.
 */
export const LANDING_SCENARIO_NOTE =
  "Scenario states come from the app-wide MSW seam; no per-page plumbing.";
